import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert, Box, Button, ButtonBase, Chip, Divider, IconButton, MenuItem,
    Paper, Stack, Switch, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import BarChartIcon from '@mui/icons-material/BarChart';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { ConfigCard } from '../style/cards';

// ────────────────────────────────────────────────────────────────────────
// Static option pools
// ────────────────────────────────────────────────────────────────────────
const COOKIE_CATEGORIES = [
    { key: 'necessary', icon: <LockOutlinedIcon     fontSize="small" />, required: true,
      i18nKey: 'cfg_overige_cookie_cat_necessary',
      titleFallback: 'Necessary & functional',
      descFallback:  'Essential for the shop to work — login, cart, checkout. Cannot be turned off.' },
    { key: 'analytics', icon: <BarChartIcon         fontSize="small" />, required: false,
      i18nKey: 'cfg_overige_cookie_cat_analytics',
      titleFallback: 'Analytics',
      descFallback:  'Help understand how visitors use the shop — page views, traffic sources, popular products.' },
    { key: 'marketing', icon: <CampaignOutlinedIcon fontSize="small" />, required: false,
      i18nKey: 'cfg_overige_cookie_cat_marketing',
      titleFallback: 'Marketing',
      descFallback:  'Used for targeted advertising, retargeting, and personalized content.' },
];

const BANNER_STYLES = [
    { value: 'floating', i18nKey: 'cfg_overige_banner_style_floating', fallback: 'Floating cloud' },
    { value: 'modal',    i18nKey: 'cfg_overige_banner_style_modal',    fallback: 'Small modal'    },
    { value: 'bottom',   i18nKey: 'cfg_overige_banner_style_bottom',   fallback: 'Bottom bar'     },
];
const POSITIONS = [
    { value: 'top',    i18nKey: 'cfg_overige_pos_top',    fallback: 'Top'    },
    { value: 'bottom', i18nKey: 'cfg_overige_pos_bottom', fallback: 'Bottom' },
];

// Pre-defined tracking tools shoppers can plug an ID into. Anything else
// belongs in the "Custom code" card.
const TRACKING_TOOLS = [
    { key: 'ga4',          i18nKey: 'cfg_overige_tool_ga4',         titleFallback: 'Google Analytics 4',  descFallback: 'Tracks page views, events, and traffic sources', badge: 'GA',  badgeBg: '#f57c00' },
    { key: 'metaPixel',    i18nKey: 'cfg_overige_tool_meta',        titleFallback: 'Meta Pixel',          descFallback: 'Facebook & Instagram ad tracking',               badge: 'M',   badgeBg: '#1976d2' },
    { key: 'tiktokPixel',  i18nKey: 'cfg_overige_tool_tiktok',      titleFallback: 'TikTok Pixel',        descFallback: 'TikTok advertising and conversion tracking',     badge: 'T',   badgeBg: '#000000' },
    { key: 'hotjar',       i18nKey: 'cfg_overige_tool_hotjar',      titleFallback: 'Hotjar',              descFallback: 'Heatmaps, session recordings, and feedback',     badge: 'H',   badgeBg: '#fd3a5c' },
    { key: 'gtm',          i18nKey: 'cfg_overige_tool_gtm',         titleFallback: 'Google Tag Manager',  descFallback: 'Container for managing third-party tags',        badge: 'GTM', badgeBg: '#0288d1' },
];

// ────────────────────────────────────────────────────────────────────────
// Small helpers
// ────────────────────────────────────────────────────────────────────────

// Visual mini-mockup used inside the banner-style and position pickers.
const StyleTile = ({ kind, position }) => {
    const muted = 'rgba(0,0,0,0.18)';
    const accent = 'currentColor';
    if (kind === 'floating') {
        return (
            <Box sx={{ width: 38, height: 26, border: `1px solid ${muted}`, borderRadius: 0.5, position: 'relative', backgroundColor: '#fff' }}>
                <Box sx={{ position: 'absolute', left: 4, right: 4, bottom: 4, height: 8, backgroundColor: accent, borderRadius: 0.4, boxShadow: '0 1px 2px rgba(0,0,0,0.15)' }} />
            </Box>
        );
    }
    if (kind === 'modal') {
        return (
            <Box sx={{ width: 38, height: 26, border: `1px solid ${muted}`, borderRadius: 0.5, position: 'relative', backgroundColor: '#fff' }}>
                <Box sx={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', width: 14, height: 12, backgroundColor: accent, borderRadius: 0.3 }} />
            </Box>
        );
    }
    if (kind === 'bottom') {
        return (
            <Box sx={{ width: 38, height: 26, border: `1px solid ${muted}`, borderRadius: 0.5, position: 'relative', backgroundColor: '#fff' }}>
                <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 8, backgroundColor: accent }} />
            </Box>
        );
    }
    // position tile (top/bottom): box with bar at top OR bottom
    return (
        <Box sx={{ width: 38, height: 26, border: `1px solid ${muted}`, borderRadius: 0.5, position: 'relative', backgroundColor: '#fff' }}>
            <Box sx={{ position: 'absolute', left: 0, right: 0, [position === 'top' ? 'top' : 'bottom']: 0, height: 6, backgroundColor: accent }} />
        </Box>
    );
};
StyleTile.propTypes = { kind: PropTypes.string, position: PropTypes.string };

// Tile picker — used both for banner-style and vertical-position. Selected
// tile gets the blue tint, unselected stays white-outlined.
const TilePicker = ({ options, value, onChange, renderTile }) => (
    <Box sx={{ display: 'flex', gap: 1 }}>
        {options.map(opt => {
            const sel = value === opt.value;
            return (
                <ButtonBase
                    key={opt.value}
                    onClick={() => onChange(opt.value)}
                    sx={{
                        flexDirection: 'column', gap: 0.5,
                        p: 1.25, minWidth: 96,
                        borderRadius: 1.5,
                        border: '1.5px solid',
                        borderColor: sel ? 'primary.main' : 'divider',
                        backgroundColor: sel ? 'rgba(25,118,210,0.08)' : '#fff',
                        color: sel ? 'primary.main' : 'text.secondary',
                        textTransform: 'none',
                        transition: 'all 0.12s',
                    }}
                >
                    {renderTile(opt)}
                    <Typography variant="caption" sx={{ fontWeight: 600, mt: 0.5 }}>{opt.label}</Typography>
                </ButtonBase>
            );
        })}
    </Box>
);
TilePicker.propTypes = { options: PropTypes.array, value: PropTypes.string, onChange: PropTypes.func, renderTile: PropTypes.func };

// Common settings row: label + helper on the left, control on the right.
const Row = ({ label, help, control, align = 'center' }) => (
    <Box sx={{ display: 'flex', alignItems: align === 'top' ? 'flex-start' : 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
            {help && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.4 }}>{help}</Typography>}
        </Box>
        <Box sx={{ flexShrink: 0, pt: align === 'top' ? 0.25 : 0 }}>{control}</Box>
    </Box>
);
Row.propTypes = { label: PropTypes.node, help: PropTypes.node, control: PropTypes.node, align: PropTypes.string };

// ────────────────────────────────────────────────────────────────────────
// Main tab
// ────────────────────────────────────────────────────────────────────────
const OverigeTab = ({ theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);

    const [config, setConfig] = useState({
        cookies: {
            categories: { necessary: true, analytics: true, marketing: true },
            bannerStyle: 'bottom',
            position:    'bottom',
        },
        trackingTools: [
            { key: 'ga4',       id: 'G-X1Y2Z3ABC4',     active: true },
            { key: 'metaPixel', id: '1234567890123456', active: true },
        ],
        toolToAdd: '',
        customCode: {
            expanded: true,
            section: 'head', // 'head' | 'body'
            head: '<title>Webshop | Gratis verzending en retour vanaf €100 </title>\n\n<!-- Default Meta Tags -->\n<meta name="title" content="Webshop | Gratis verzending en retour vanaf €100 ">\n<meta name="description" content="Webshop">\n<meta name="keywords" content="Gratis verzending en retour vanaf € 100">\n<meta name="url" content="testshop.fashionretail.be">\n<meta name="image" content="img-repository/original-1701261777-LogoPaars.png">\n\n<!-- Google / Search Engine Tags -->',
            body: '',
        },
    });
    const setSection = (k) => (v) => setConfig(p => ({ ...p, [k]: { ...(p[k] || {}), ...v } }));

    const setCookies = setSection('cookies');
    const setCustomCode = setSection('customCode');

    const toggleCategory = (key, value) => {
        setCookies({ categories: { ...config.cookies.categories, [key]: value } });
    };

    const addTool = () => {
        if (!config.toolToAdd) return;
        if (config.trackingTools.find(t => t.key === config.toolToAdd)) return;
        setConfig(p => ({
            ...p,
            trackingTools: [...p.trackingTools, { key: config.toolToAdd, id: '', active: true }],
            toolToAdd: '',
        }));
    };
    const removeTool = (key) => setConfig(p => ({ ...p, trackingTools: p.trackingTools.filter(t => t.key !== key) }));
    const updateTool = (key, patch) => setConfig(p => ({
        ...p,
        trackingTools: p.trackingTools.map(t => t.key === key ? { ...t, ...patch } : t),
    }));
    const availableTools = TRACKING_TOOLS.filter(t => !config.trackingTools.find(x => x.key === t.key));

    return (
        <Box>
            {/* Page header — matches the schema's "Cookies & tracking" title row */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {tr('cfg_overige_panel_title', 'Cookies & tracking')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {tr('cfg_overige_panel_desc', 'Configure how cookie consent is asked from visitors, and manage tracking scripts like Google Analytics, Meta Pixel, or your own custom code.')}
                </Typography>
            </Box>

            <Stack spacing={2}>
                {/* ────── Cookie consent ────── */}
                <ConfigCard
                    title={tr('cfg_overige_consent_title', 'Cookie consent')}
                    description={tr('cfg_overige_consent_desc', 'What visitors see when they first arrive on your site. Required for GDPR and other privacy regulations.')}
                    theme={theme}
                >
                    {/* Categories */}
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {tr('cfg_overige_cat_title', 'Cookie categories visitors can choose from')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.25, lineHeight: 1.4 }}>
                            {tr('cfg_overige_cat_help', 'Visitors can opt in or out of these categories. Necessary cookies are always required for the site to function.')}
                        </Typography>
                        <Stack spacing={1}>
                            {COOKIE_CATEGORIES.map(cat => {
                                const checked = config.cookies.categories[cat.key];
                                return (
                                    <Paper key={cat.key} variant="outlined" sx={{
                                        p: 1.5, borderRadius: 1.5,
                                        backgroundColor: cat.required ? 'rgba(25,118,210,0.08)' : '#fff',
                                        borderColor: cat.required ? 'primary.light' : 'divider',
                                        display: 'flex', alignItems: 'center', gap: 1.5,
                                    }}>
                                        <Box sx={{
                                            width: 32, height: 32, borderRadius: 1,
                                            backgroundColor: 'rgba(0,0,0,0.04)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: 'text.secondary', flexShrink: 0,
                                        }}>{cat.icon}</Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                    {tr(cat.i18nKey, cat.titleFallback)}
                                                </Typography>
                                                {cat.required && (
                                                    <Chip
                                                        label={tr('cfg_overige_required', 'REQUIRED')}
                                                        size="small"
                                                        color="primary"
                                                        sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, '& .MuiChip-label': { px: 0.75 } }}
                                                    />
                                                )}
                                            </Box>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.35 }}>
                                                {tr(`${cat.i18nKey}_desc`, cat.descFallback)}
                                            </Typography>
                                        </Box>
                                        <Switch
                                            color="success"
                                            size="small"
                                            checked={!!checked}
                                            disabled={cat.required}
                                            onChange={(e) => toggleCategory(cat.key, e.target.checked)}
                                        />
                                    </Paper>
                                );
                            })}
                        </Stack>
                    </Box>

                    <Divider />

                    <Row
                        label={tr('cfg_overige_banner_style', 'Banner style')}
                        help={tr('cfg_overige_banner_style_help', 'How the consent prompt appears to visitors. Click to preview each option.')}
                        align="top"
                        control={
                            <TilePicker
                                options={BANNER_STYLES.map(o => ({ ...o, label: tr(o.i18nKey, o.fallback) }))}
                                value={config.cookies.bannerStyle}
                                onChange={(v) => setCookies({ bannerStyle: v })}
                                renderTile={(opt) => <StyleTile kind={opt.value} />}
                            />
                        }
                    />

                    <Divider />

                    <Row
                        label={tr('cfg_overige_position', 'Vertical position')}
                        help={tr('cfg_overige_position_help', 'Whether the prompt sits at the top or bottom of the page')}
                        align="top"
                        control={
                            <TilePicker
                                options={POSITIONS.map(o => ({ ...o, label: tr(o.i18nKey, o.fallback) }))}
                                value={config.cookies.position}
                                onChange={(v) => setCookies({ position: v })}
                                renderTile={(opt) => <StyleTile kind="position" position={opt.value} />}
                            />
                        }
                    />

                    <Divider />

                    {/* Live preview */}
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {tr('cfg_overige_preview_title', 'Live preview')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1, lineHeight: 1.4 }}>
                            {tr('cfg_overige_preview_help', 'Updates as you change the banner style and position above')}
                        </Typography>
                        <CookiePreview
                            style={config.cookies.bannerStyle}
                            position={config.cookies.position}
                            tr={tr}
                        />
                    </Box>
                </ConfigCard>

                {/* ────── Tracking tools ────── */}
                <ConfigCard
                    title={tr('cfg_overige_tools_title', 'Tracking tools')}
                    description={tr('cfg_overige_tools_desc', "Add common analytics and marketing tools by entering just an ID — we generate the script tags for you. For tools not in this list, use the 'Custom code' section below.")}
                    theme={theme}
                >
                    <Stack spacing={1}>
                        {config.trackingTools.map(t => {
                            const meta = TRACKING_TOOLS.find(x => x.key === t.key);
                            if (!meta) return null;
                            return (
                                <Paper key={t.key} variant="outlined" sx={{
                                    p: 1.25, borderRadius: 1.5,
                                    backgroundColor: 'rgba(25,118,210,0.06)',
                                    borderColor: 'primary.light',
                                    display: 'grid',
                                    gridTemplateColumns: 'auto 1fr 220px auto auto',
                                    alignItems: 'center', gap: 1.5,
                                }}>
                                    <Box sx={{
                                        width: 36, height: 36, borderRadius: 1,
                                        backgroundColor: meta.badgeBg,
                                        color: '#fff',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontWeight: 700, fontSize: '0.85rem',
                                        flexShrink: 0,
                                    }}>{meta.badge}</Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                            {tr(meta.i18nKey, meta.titleFallback)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.35 }}>
                                            {tr(`${meta.i18nKey}_desc`, meta.descFallback)}
                                        </Typography>
                                    </Box>
                                    <TextField
                                        size="small"
                                        value={t.id}
                                        onChange={(e) => updateTool(t.key, { id: e.target.value })}
                                        sx={{ backgroundColor: '#fff', borderRadius: 1 }}
                                    />
                                    <Chip
                                        label={t.active ? tr('cfg_overige_active', 'Active') : tr('cfg_overige_inactive', 'Inactive')}
                                        size="small"
                                        color={t.active ? 'success' : 'default'}
                                        sx={{ height: 22, fontWeight: 600 }}
                                    />
                                    <IconButton size="small" onClick={() => removeTool(t.key)} sx={{ color: 'text.secondary' }}>
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Paper>
                            );
                        })}

                        {/* Add tool row */}
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <TextField
                                select size="small" fullWidth
                                value={config.toolToAdd}
                                onChange={(e) => setConfig(p => ({ ...p, toolToAdd: e.target.value }))}
                                placeholder={tr('cfg_overige_choose_tool', 'Choose a tool to add…')}
                                displayEmpty
                                SelectProps={{ displayEmpty: true }}
                            >
                                <MenuItem value="">
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        {tr('cfg_overige_choose_tool', 'Choose a tool to add…')}
                                    </Typography>
                                </MenuItem>
                                {availableTools.map(t => (
                                    <MenuItem key={t.key} value={t.key}>
                                        {tr(t.i18nKey, t.titleFallback)}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Button
                                variant="contained"
                                onClick={addTool}
                                disabled={!config.toolToAdd}
                                sx={{ textTransform: 'none', flexShrink: 0 }}
                            >
                                {tr('cfg_overige_add_tool', 'Add tool')}
                            </Button>
                        </Box>
                    </Stack>
                </ConfigCard>

                {/* ────── Custom code (collapsible) ────── */}
                <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                    <ButtonBase
                        onClick={() => setCustomCode({ expanded: !config.customCode.expanded })}
                        sx={{
                            width: '100%', p: 2.5,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            textAlign: 'left',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {config.customCode.expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {tr('cfg_overige_custom_title', 'Custom code')}
                            </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {tr('cfg_overige_custom_subtitle', 'For advanced users — paste raw HTML, scripts, or meta tags')}
                        </Typography>
                    </ButtonBase>

                    {config.customCode.expanded && (
                        <Box sx={{ p: 2.5, pt: 0 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5, lineHeight: 1.4 }}>
                                {tr('cfg_overige_custom_desc', 'Anything pasted here is added to your site\'s pages. The two tabs below correspond to the two places where scripts can run: in the document head (for tags loaded before the page renders) or just before the closing body tag (for tags that should load after the content).')}
                            </Typography>

                            <Alert severity="warning" sx={{ mb: 1.5 }}>
                                {tr('cfg_overige_custom_warning', 'Anything pasted here runs on every page of your site. Mistakes here can break the entire shop — only paste code you trust and have tested.')}
                            </Alert>

                            <Tabs
                                value={config.customCode.section}
                                onChange={(_e, v) => setCustomCode({ section: v })}
                                sx={{
                                    minHeight: 36,
                                    borderBottom: '1px solid', borderColor: 'divider',
                                    '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontSize: '0.85rem', fontWeight: 600 },
                                }}
                            >
                                <Tab
                                    value="head"
                                    label={
                                        <Box component="span">
                                            <Box component="code" sx={{ fontFamily: 'monospace', mr: 0.75 }}>{'<head>'}</Box>
                                            <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                                                — {tr('cfg_overige_custom_head_help', 'loads before the page renders')}
                                            </Typography>
                                        </Box>
                                    }
                                />
                                <Tab
                                    value="body"
                                    label={
                                        <Box component="span">
                                            <Box component="code" sx={{ fontFamily: 'monospace', mr: 0.75 }}>{'<body>'}</Box>
                                            <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                                                — {tr('cfg_overige_custom_body_help', 'loads at the end of the page')}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Tabs>

                            <TextField
                                multiline
                                minRows={10}
                                fullWidth
                                value={config.customCode[config.customCode.section]}
                                onChange={(e) => setCustomCode({ [config.customCode.section]: e.target.value })}
                                placeholder={`<!-- Paste your ${config.customCode.section === 'head' ? '<head>' : '<body>'} code here -->`}
                                sx={{
                                    mt: 1.5,
                                    '& .MuiInputBase-root':  { p: 1.5 },
                                    '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem', lineHeight: 1.5 },
                                }}
                            />
                        </Box>
                    )}
                </Paper>
            </Stack>
        </Box>
    );
};

OverigeTab.propTypes = { theme: PropTypes.string, t: PropTypes.func };

// ────────────────────────────────────────────────────────────────────────
// Cookie banner live preview — fake browser frame with the banner inserted
// at the chosen position, in the chosen style.
// ────────────────────────────────────────────────────────────────────────
const CookiePreview = ({ style, position, tr }) => {
    const banner = (
        <Box sx={{
            backgroundColor: '#1a1a1a',
            color: '#fff',
            px: 2, py: 1.25,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 2,
            fontSize: '0.8rem',
            ...(style === 'floating' && {
                mx: 2, mb: 1.5,
                borderRadius: 1.5,
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            }),
            ...(style === 'modal' && {
                mx: 'auto', my: 2,
                width: '70%',
                borderRadius: 1.5,
            }),
        }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {tr('cfg_overige_preview_text', 'We use cookies to improve your shopping experience. Essential cookies are required; analytics and marketing are optional.')}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
                <Box sx={{ px: 1.25, py: 0.5, borderRadius: 0.75, border: '1px solid rgba(255,255,255,0.3)', fontSize: '0.7rem', fontWeight: 600 }}>
                    {tr('cfg_overige_preview_customize', 'Customize')}
                </Box>
                <Box sx={{ px: 1.25, py: 0.5, borderRadius: 0.75, border: '1px solid rgba(255,255,255,0.3)', fontSize: '0.7rem', fontWeight: 600 }}>
                    {tr('cfg_overige_preview_reject', 'Reject')}
                </Box>
                <Box sx={{ px: 1.25, py: 0.5, borderRadius: 0.75, backgroundColor: '#fff', color: '#000', fontSize: '0.7rem', fontWeight: 700 }}>
                    {tr('cfg_overige_preview_accept', 'Accept all')}
                </Box>
            </Box>
        </Box>
    );

    return (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', backgroundColor: 'rgba(25,118,210,0.04)', p: 2 }}>
            <Box sx={{ borderRadius: 1.5, border: '1px solid', borderColor: 'divider', backgroundColor: '#fff', overflow: 'hidden' }}>
                {/* Browser chrome */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.75, backgroundColor: 'rgba(0,0,0,0.04)', borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ccc' }} />
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ccc' }} />
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ccc' }} />
                    <Box sx={{
                        ml: 1, flex: 1,
                        backgroundColor: '#fff',
                        borderRadius: 0.75,
                        px: 1.25, py: 0.4,
                        border: '1px solid', borderColor: 'divider',
                        fontSize: '0.7rem',
                        color: 'text.secondary',
                    }}>testshop.fashionretail.be</Box>
                </Box>

                {/* Page body — banner in place if position=top */}
                {position === 'top' && banner}
                <Box sx={{ p: 2, minHeight: 100 }}>
                    <Box sx={{ height: 4, width: '90%', backgroundColor: 'rgba(0,0,0,0.06)', borderRadius: 0.4, mb: 0.75 }} />
                    <Box sx={{ height: 4, width: '60%', backgroundColor: 'rgba(0,0,0,0.06)', borderRadius: 0.4 }} />
                </Box>
                {position === 'bottom' && banner}
            </Box>
        </Paper>
    );
};
CookiePreview.propTypes = { style: PropTypes.string, position: PropTypes.string, tr: PropTypes.func };

export default OverigeTab;
